import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ToastContainer } from 'react-toastify';
import { DocumentTitle, Sidebar, ProtectedRoute } from './components/layout';
import { ErrorBoundary } from './components/common';
import { useCrossTabAuthSync, useTheme } from './hooks';
import {
  Login,
  Register,
  Dashboard,
  Workflows,
  WorkflowBuilder,
  Widgets,
  Dashboards,
  DashboardBuilder,
  Assignments,
  TaskExecution,
  FileViewer,
  Summary,
  UnifiedDashboard,
  JoinTeam,
  Team,
  Settings,
  MicrosoftCallback,
  Assets,
  Triggers,
  NotFound,
} from './pages';
import './App.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function App() {
  useCrossTabAuthSync();
  useTheme();

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <DocumentTitle />
          <Routes>
            {/* Chromeless routes — no navbar */}
            <Route
              path="/viewer/:assignmentId/:widgetId/:fileId"
              element={
                <ProtectedRoute>
                  <div className="h-dvh bg-neutral-900"><FileViewer /></div>
                </ProtectedRoute>
              }
            />

            {/* All other routes — with sidebar */}
            <Route path="*" element={
              <div className="flex flex-row h-dvh bg-neutral-50">
                <Sidebar />
                <div className="flex-1 min-w-0 min-h-0 overflow-hidden">
                  <Routes>
                    {/* Public Routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/auth/microsoft/callback" element={<MicrosoftCallback />} />

                {/* Protected Routes */}
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <UnifiedDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/workflows"
                  element={
                    <ProtectedRoute allowedRoles={['owner', 'admin']}>
                      <Workflows />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/workflow-builder"
                  element={
                    <ProtectedRoute allowedRoles={['owner', 'admin']}>
                      <WorkflowBuilder />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/workflow-builder/:workflowId"
                  element={
                    <ProtectedRoute allowedRoles={['owner', 'admin']}>
                      <WorkflowBuilder />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/widgets"
                  element={
                    <ProtectedRoute allowedRoles={['owner', 'admin']}>
                      <Widgets />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboards"
                  element={
                    <ProtectedRoute allowedRoles={['owner', 'admin']}>
                      <Dashboards />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard-builder"
                  element={
                    <ProtectedRoute allowedRoles={['owner', 'admin']}>
                      <DashboardBuilder />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard-builder/:dashboardId"
                  element={
                    <ProtectedRoute allowedRoles={['owner', 'admin']}>
                      <DashboardBuilder />
                    </ProtectedRoute>
                  }
                />
                <Route path="/assignments" element={<Navigate to="/dashboard" replace />} />
                <Route
                  path="/task/:assignmentId"
                  element={
                    <ProtectedRoute>
                      <TaskExecution />
                    </ProtectedRoute>
                  }
                />
                <Route path="/summary" element={<Navigate to="/dashboard" replace />} />
                <Route path="/monitor" element={<Navigate to="/dashboard" replace />} />
                <Route
                  path="/join-team"
                  element={
                    <ProtectedRoute allowedRoles={['worker']}>
                      <JoinTeam />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/team"
                  element={
                    <ProtectedRoute allowedRoles={['owner', 'admin']}>
                      <Team />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/coverage"
                  element={
                    <ProtectedRoute>
                      <Assets />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/triggers"
                  element={
                    <ProtectedRoute allowedRoles={['owner', 'admin']}>
                      <Triggers />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <Settings />
                    </ProtectedRoute>
                  }
                />
                <Route path="/profile" element={<Navigate to="/settings" replace />} />

                {/* Default redirect - Dashboard is the landing page */}
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="*" element={<NotFound />} />
                  </Routes>
                </div>
              </div>
            } />
          </Routes>
          <ToastContainer stacked />
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
